import {
    Avatar,
    Button,
    DarkModeToggle,
    Icon,
    IconLib,
    Menu,
    MenuDivider,
    MenuHeading,
    MenuItem,
    MenuSection,
    Text,
    useTheme,
    View,
} from '@fold-dev/core'
import { useMemo } from 'react'
import {
    TbCube3dSphere,
    TbFilters,
    TbLayoutGrid,
    TbLifebuoy,
    TbLogout,
    TbSettings,
    TbSpeakerphone,
    TbSquareRoundedPlus,
    TbTag,
    TbUserSquareRounded,
} from 'react-icons/tb'
import { useParams } from 'react-router'
import { useAuth } from '../hooks/auth.hook'
import { useAppStore } from '../store/app'
import { Dock } from '../components'
import { DockToggle } from '../layouts/app.layout'
import { PresenceAvatar } from '../components/common/presence-avatar'

declare global {
    const BUILD_DATE: string
}

export const WorkspaceMenu = ({
    hide,
    workspaces,
    onSettings,
    onLabels,
    onMembers,
    onAddTeam,
    onAccount,
    onHelp,
    onAddWorkspace,
    onProjects,
}) => {
    const { getMe, logout } = useAuth()
    const { workspaceId } = useParams()
    const workspace = useAppStore((state) => state.workspace)
    const { isOwner, isMember, isGuest } = useMemo(
        () => ({
            isOwner: workspace.role == 'Owner' || workspace.role == 'Admin',
            isMember: workspace.role == 'Member',
            isGuest: workspace.role == 'Guest',
        }),
        [workspace.role]
    )
    const user = useAppStore((state) => state.user)
    const { systemTheme } = useTheme()

    const handleWorkspaceChange = (workspace) => {
        window.location.href = `/app/workspace/${workspace.id}/inbox`
    }

    return (
        <Menu
            className="nav-header__menu"
            width="100%"
            border="none">
            <MenuHeading>{workspace.name}</MenuHeading>
            <MenuItem
                disabled={isGuest}
                prefix={<Icon icon={TbTag} />}
                onClick={() => {
                    hide()
                    onLabels()
                }}>
                Labels
            </MenuItem>
            <MenuItem
                disabled={!isOwner}
                prefix={<Icon icon={TbSettings} />}
                onClick={() => {
                    onSettings()
                    hide()
                }}>
                Settings
            </MenuItem>
            <MenuItem
                disabled={!isOwner}
                prefix={<Icon icon={TbUserSquareRounded} />}
                onClick={() => {
                    onMembers()
                    hide()
                }}>
                People & Teams
            </MenuItem>
            <MenuItem
                disabled={isGuest}
                prefix={<Icon icon={TbLayoutGrid} />}
                onClick={() => {
                    hide()
                    onProjects()
                }}>
                Projects
            </MenuItem>
            <MenuDivider />
            <MenuItem
                disabled={isGuest}
                prefix={<Icon icon={TbFilters} />}
                onClick={() => {
                    onAddTeam()
                }}>
                Create Team
            </MenuItem>
            <MenuDivider />
            <MenuItem
                prefix={
                    <PresenceAvatar
                        size="xs"
                        userId={user.id}
                        src={user.image}
                        name={user.name}
                    />
                }
                onClick={() => {
                    onAccount()
                    hide()
                }}>
                Your Account
            </MenuItem>
            {/*  <MenuItem
                prefix={<Icon icon={TbLifebuoy} />}
                onClick={() => {
                    onHelp()
                    hide()
                }}>
                Help & Support
            </MenuItem> */}
            <MenuItem
                prefix={<Icon icon={TbLogout} />}
                onClick={() => {
                    logout()
                    hide()
                }}>
                Sign out
            </MenuItem>
            <MenuDivider />
            <MenuItem
                prefix={<Icon icon={TbCube3dSphere} />}
                suffix={<IconLib icon="more-h" />}
                menu={
                    <Menu>
                        {workspaces.map((workspace: any, index: number) => (
                            <MenuItem
                                key={index}
                                onClick={() => {
                                    hide()
                                    handleWorkspaceChange(workspace)
                                }}
                                prefix={
                                    <Avatar
                                        src={workspace.image}
                                        name={workspace.name}
                                        size="xs"
                                        style={{
                                            outlineOffset: 2,
                                            outline:
                                                workspace.id == workspaceId
                                                    ? '2px solid var(--f-color-accent-400)'
                                                    : null,
                                        }}
                                    />
                                }>
                                {workspace.name}
                            </MenuItem>
                        ))}
                    </Menu>
                }>
                Workspaces
            </MenuItem>
            <MenuItem
                prefix={<Icon icon={TbSquareRoundedPlus} />}
                onClick={() => {
                    hide()
                    onAddWorkspace()
                }}>
                Create Workspace
            </MenuItem>
            <MenuDivider />
            <MenuItem
                prefix={<DockToggle />}
                style={{ textTransform: 'capitalize' }}>
                Dock
            </MenuItem>
            <MenuItem
                prefix={<DarkModeToggle />}
                style={{ textTransform: 'capitalize' }}>
                {systemTheme} mode
            </MenuItem>
            <MenuDivider />
            <MenuSection>
                <View
                    column
                    gap={8}
                    radius="var(--f-radius)"
                    p="0.75rem 1rem"
                    alignContent="flex-start"
                    alignItems="flex-start"
                    //</MenuSection>bg="linear-gradient(165deg, var(--f-color-indigo-600), var(--f-color-purple-500))"
                    //</Menu>border="2px solid var(--f-color-indigo-600)"
                    bgToken="highlight-weak">
                    {/* <Text
                        fontWeight="bold"
                        colorToken="highlight">
                        Early Access
                    </Text> */}
                    <Text
                        size="sm"
                        colorToken="highlight">
                        Thank you for using Fronteer Early Access.{' '}
                        {/* Please report any bugs or issues to{' '}
                        <a
                            href="mailto:support@fronteer.app"
                            style={{ color: 'inherit' }}>
                            support@fronteer.app
                        </a> */}
                        .
                    </Text>
                    <Button
                        onClick={(e) => {
                            hide()
                            window['fronteer'](e.target)
                        }}
                        data-shortcode="7c3ffd37-5b5e-4d1e-abe8-cef148332661"
                        data-theme="light"
                        size="sm"
                        variant="highlight"
                        width="100%">
                        Give Feedback
                    </Button>
                    <Text
                        size="sm"
                        colorToken="highlight">
                        v{BUILD_DATE}
                    </Text>
                </View>
            </MenuSection>
        </Menu>
    )
}
